import { useState, useEffect, useContext, useMemo, useCallback, memo } from 'react';
import { EditorState, convertToRaw, convertFromRaw, RichUtils, Modifier } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import axios from 'axios';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import '../../styles/App.css';
import { Button, Spinner } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { IoIosClose } from "react-icons/io";
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import translations from "./translations";
// import ChapterPreview from './ChapterPreview';
import { useAlertContext } from '../../contexts/AlertContext';
import { AuthContext } from '../../contexts/AuthContext';

const useCustomBlockRenderer = (editorState) => {
  return useCallback((contentBlock) => {
    if (contentBlock.getType() === 'atomic') {
      const entityKey = contentBlock.getEntityAt(0);
      if (entityKey) {
        const entity = editorState.getCurrentContent().getEntity(entityKey);
        if (entity.getType() === 'IMAGE') {
          const { src } = entity.getData();
          return {
            component: (props) => (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '10px 0'
              }}>
                <img
                  src={src}
                  alt="uploaded"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                  }}
                />
              </div>
            ),
            editable: false,
          };
        }
      }
    }
    return null;
  }, [editorState]);
};


const DiffViewer = memo(({ oldEditorState, newEditorState, t }) => {
  const getPlainTextContent = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const rawContent = convertToRaw(contentState);
    const { blocks, entityMap } = rawContent;

    return blocks.map(block => {
      if (block.type === 'atomic') {
        const entityKey = block.entityRanges[0]?.key;
        if (entityKey !== null && entityMap[entityKey]) {
          const entity = entityMap[entityKey];
          return `[IMAGE-SIZE:${entity.data.src.length}]`;
        }
        return '[IMAGE]';
      }
      return block.text || '';
    });
  };

  const oldContent = getPlainTextContent(oldEditorState);
  const newContent = getPlainTextContent(newEditorState);

  const formatContent = (blocks) => {
    return blocks.join('\n');
  };

  return (
    <div className="style-diff-container">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6>{t.textComparison || 'Comparação de Texto'}</h6>
      </div>

      <ReactDiffViewer
        oldValue={formatContent(oldContent)}
        newValue={formatContent(newContent)}
        splitView={true}
        showDiffOnly={true}
        compareMethod={DiffMethod.WORDS_WITH_SPACE}
        leftTitle={t.originalVersion || 'Versão Original'}
        rightTitle={t.currentVersion || 'Versão Atual'}
        useDarkTheme={false}
        hideLineNumbers={false}
        styles={{
          variables: {
            light: {
              addedBackground: '#d1f2eb',
              addedColor: '#00695c',
              removedBackground: '#fdeaea',
              removedColor: '#d32f2f',
              wordAddedBackground: '#a7f3d0',
              wordRemovedBackground: '#fecaca',
              emptyLineBackground: '#fafafa',
            },
          },
          line: {
            padding: '10px 2px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          },
          marker: {
            width: '25px',
          },
          content: {
            width: 'auto',
          },
          contentText: {
            fontSize: '14px',
            lineHeight: '1.6',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          },
        }}
      />
    </div>
  );
});

const handleKeyCommand = (command, editorState, setEditorState) => {
  if (command === 'backspace' || command === 'delete') {
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const blockKey = selection.getStartKey();
    const block = contentState.getBlockForKey(blockKey);

    if (block.getType() === 'atomic') {
      const newContentState = Modifier.removeRange(
        contentState,
        selection.merge({
          anchorOffset: 0,
          focusOffset: block.getLength(),
        }),
        'backward'
      );

      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        'remove-range'
      );

      setEditorState(newEditorState);
      return 'handled';
    }
  }
  return 'not-handled';
};

const handleTabKey = (e, editorState, setEditorState) => {
  e.preventDefault();

  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(selection.getStartKey());

  if (block.getType().indexOf('list-item') === -1) {
    const newContentState = Modifier.replaceText(
      contentState,
      selection,
      '\t'
    );

    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'insert-characters'
    );

    setEditorState(newEditorState);
  } else {
    const newState = RichUtils.onTab(e, editorState, 4);
    if (newState) {
      setEditorState(newState);
    }
  }
};

function ChapterTextEditor({ auth, entity, oldEntity, onBack, readOnly = false }) {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const [diff, setDiff] = useState(EditorState.createEmpty());
  const [showDiff, setShowDiff] = useState(false);
  // const [currentPage, setCurrentPage] = useState(1);
  // const [previewActive, setPreviewActive] = useState(false);
  const { language } = useContext(LanguageContext);
  const [tag, setTag] = useState([]);
  const [inputValue, setInputValue] = useState('');


  // ALTERAÇÃO 2: Acessar o username do AuthContext
  const { username } = useContext(AuthContext);

  const [loadingSave, setLoadingSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const customBlockRenderer = useCustomBlockRenderer(editorState);

  // console.warn('Renderizando ChapterTextEditor...');

  // Custom hook for alerts
  const { showSuccess, showError } = useAlertContext();

  // Select the current language's translations
  const t = translations[language] || translations.pt;

  useEffect(() => {
    setIsLoading(true);

    if (entity.content) {
      try {
        const parsedContent = JSON.parse(entity.content);
        const contentState = convertFromRaw(parsedContent);
        const newEditorState = EditorState.createWithContent(contentState);

        setEditorState(newEditorState);
        if (oldEntity && oldEntity.content) {
          const oldParsedContent = JSON.parse(oldEntity.content);
          const oldContentState = convertFromRaw(oldParsedContent);
          const oldEditorState = EditorState.createWithContent(oldContentState);
          setDiff(oldEditorState);
        } else {
          setDiff(newEditorState);
        }

      } catch (error) {
        const emptyState = EditorState.createEmpty();
        setEditorState(emptyState);
      } finally {
        setIsLoading(false);
      }
    } else {
      const emptyState = EditorState.createEmpty();
      setEditorState(emptyState);
      setIsLoading(false);
    }
  }, [entity.content, oldEntity]);

  const onKeyCommand = useCallback((command) => {
    return handleKeyCommand(command, editorState, setEditorState);
  }, [editorState]);

  const onTab = useCallback((e) => {
    return handleTabKey(e, editorState, setEditorState);
  }, [editorState]);

  const isTask = !!entity.referenceNumber;
  const endpoint = isTask ? API_ENDPOINTS.TASKS_BY_ID_CONTENT(entity.id) : API_ENDPOINTS.CHAPTERS_BY_ID(entity.id);
  const title = isTask ? `${t.task} #${entity.referenceNumber} - ${entity.chapter.title}` : entity.title;

  const handleSave = useCallback(async () => {
    setLoadingSave(true);
    if (readOnly) return;

    const contentState = editorState.getCurrentContent();
    const rawContent = JSON.stringify(convertToRaw(contentState));
    const auth = localStorage.getItem('auth');

    try {
      // ALTERAÇÃO 3: Adicionar o campo 'lastModifiedBy' (ou outro de sua preferência) no corpo da requisição
      const payload = isTask ? rawContent : {
        id: entity.id,
        title: entity.title,
        content: rawContent,
        owner: entity.owner || null,
        visible: entity.visible || null,
        context: entity.context || null,
        tags: tag.map((itag) => ({
          id: itag.id,
          description: itag.value,
        })),
        lastUpdated: username, // <-- AQUI ESTÁ A INFORMAÇÃO DO USUÁRIO
      };

      await axios.put(endpoint,
        payload,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      showSuccess(t.contentUpdated);
      setDiff(editorState);

    } catch (error) {
      showError(`${t.updateFailed}: ${error.message}`);
    } finally {
      setLoadingSave(false);
    }
  }, [editorState, endpoint, isTask, entity, tag, showSuccess, showError, t, readOnly, username]);

  const handleEditorChange = useCallback((newEditorState) => {
    setEditorState(newEditorState);
  }, []);

  const uploadImageCallBack = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve({ data: { link: event.target.result } });
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }, []);

  // const customBlockRenderer = (contentBlock) => {
  //   if (contentBlock.getType() === 'atomic') {
  //     const entityKey = contentBlock.getEntityAt(0);
  //     if (entityKey) {
  //       const entity = editorState.getCurrentContent().getEntity(entityKey);
  //       if (entity.getType() === 'IMAGE') {
  //         const { src } = entity.getData();
  //         return {
  //           component: (props) => (
  //             <div style={{
  //               display: 'flex',
  //               justifyContent: 'center',
  //               alignItems: 'center',
  //               margin: '10px 0'
  //             }}>
  //               <img
  //                 src={src}
  //                 alt="uploaded"
  //                 style={{
  //                   maxWidth: '100%',
  //                   height: 'auto',
  //                 }}
  //               />
  //             </div>
  //           ),
  //           editable: false,
  //         };
  //       }
  //     }
  //   }
  //   return null;
  // };

  const toggleDiffView = () => {
    setShowDiff(!showDiff);
  };

  const handleAddTag = () => {
    if (inputValue.trim()) {
      setTag([...tag, { id: null, value: inputValue.trim() }]);
      setInputValue('');
    }
  };

  const handleRemoveTag = (indexToRemove) => {
    setTag(tag.filter((_, index) => index !== indexToRemove));
  };

  useEffect(() => {
    if (entity?.tags && Array.isArray(entity.tags)) {
      const formattedTags = entity.tags.map((tag) => ({
        id: tag.id || null,
        value: tag.description,
      }));
      setTag(formattedTags);
    } else {
      setTag([]);
    }
  }, [entity]);

  const toolbarConfig = useMemo(() => ({
    options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'image', 'history'],
    textAlign: {
      inDropdown: true,
      options: ['left', 'center', 'right', 'justify']
    },
    image: {
      uploadCallback: uploadImageCallBack,
      previewImage: true,
      alt: { present: true, mandatory: false }
    }
  }), [uploadImageCallBack]);

  return (
    <div className="container bg-secondary border rounded" style={{ "--bs-bg-opacity": 0.3 }}>
      {isLoading ? (
        <div className="text-center py-5 d-flex align-items-center justify-content-center">
          <Spinner animation="border" role="status" className="me-2">
            <span className="visually-hidden">{t.loadingData}</span>
          </Spinner>
          <span>{t.loadingData}</span>
        </div>
      ) : (
        <>
          {!isTask && (
            <>
              <input
                id="tagInput"
                className="mt-3 form-inline w-md-25 w-sm-50"
                type="text"
                placeholder={t.tagPlaceholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                style={{ padding: '8px', marginRight: '8px' }}
              />
              <button
                className="btn btn-primary mb-1"
                type="button"
                onClick={handleAddTag}
                style={{ padding: '8px 16px' }}
              >
                {t.addTag}
              </button>
            </>
          )}

          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
            {tag.map((itag, index) => (
              <div
                key={`${itag}-${index}`}
                style={{
                  display: 'inline-block',
                  position: 'relative',
                  margin: '4px',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    padding: '6px 12px 6px 12px',
                    backgroundColor: '#d1e7dd',
                    borderRadius: '12px',
                    color: '#0f5132',
                    fontWeight: 'bold',
                    border: '1px solid #0f5132',
                  }}
                >
                  {itag.value}
                </span>
                <button
                  onClick={() => handleRemoveTag(index)}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    width: '20px',
                    height: '20px',
                    padding: '0',
                    margin: '0',
                    backgroundColor: '#f5f5dc',
                    color: '#000',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #000',
                  }}
                >
                  <IoIosClose size={20} />
                </button>
              </div>
            ))}
          </div>

          <h3>{title}</h3>

          <div className='preview-container' style={{
            maxHeight: '298mm',
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}>
            <Editor
              editorState={editorState}
              onEditorStateChange={readOnly ? undefined : handleEditorChange}
              onTab={onTab}
              handleKeyCommand={onKeyCommand}
              placeholder={t.typeHere}
              readOnly={readOnly}
              toolbarHidden={readOnly}
              localization={{ locale: language === 'en' ? 'en' : 'pt' }}
              wrapperClassName="your-wrapper-class"
              editorClassName="your-editor-class"
              toolbarClassName="your-toolbar-class"
              toolbar={toolbarConfig}
              customBlockRenderFunc={customBlockRenderer}
            />
          </div>

          {showDiff && (
            <div className="mt-3 mb-3">
              <DiffViewer
                oldEditorState={diff}
                newEditorState={editorState}
                t={t}
              />
            </div>
          )}

          {/* Área de Controles */}
          <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
            {!readOnly && (
              <div className="d-flex gap-2">
                <Button onClick={handleSave} disabled={loadingSave}>
                  {loadingSave ? <Spinner animation="border" size="sm" /> : null}
                  {t.save} {isTask ? t.task : t.chapter}
                </Button>
              </div>

            )}
            <Button
              variant="outline-info"
              onClick={toggleDiffView}
            >
              {showDiff ? (t.hideDiff || 'Ocultar Diff') : (t.showDiff || 'Mostrar Diff')}
            </Button>
            <div className="d-flex align-items-center gap-2">
              {/* {!readOnly && (
                <Form.Check
                  type="switch"
                  id="preview-switch"
                  checked={previewActive}
                >
                  <Form.Check.Input
                    className="switch-preview"
                    onChange={(e) => setPreviewActive(e.target.checked)}
                  />
                  <Form.Check.Label>{t.preview}</Form.Check.Label>
                </Form.Check>
              )} */}
              {/* enviar entity atualizando as informações de tag */}
              <Button onClick={() => onBack(
                {
                  ...entity, tags: tag.map((itag) => ({
                    id: itag.id,
                    description: itag.value,
                  }))
                }
              )} variant="secondary">
                {t.back}
              </Button>
            </div>
          </div>

          {/* {previewActive && (
            <ChapterPreview
              editorState={editorState}
              currentPage={currentPage}
              totalPages={totalPages}
              setTotalPages={setTotalPages}
              setCurrentPage={setCurrentPage}
              setPreviewLoading={setLoading}
              t={t}
            />
          )} */}
        </>)}
    </div>
  );
}

export default ChapterTextEditor;
